import { queryOptions, useMutation } from "@tanstack/react-query"

import { apiKey } from "@/lib/api/changes"
import { apiFetch } from "@/lib/api/client"
import type { ApiResource } from "@/lib/api/types"
import type {
  AgreementPlaceholder,
  AgreementTemplate,
  ApiAgreementPlaceholder,
  ApiAgreementTemplate,
  PublishAgreementTemplate,
} from "@/types/agreement-template"

function toAgreementTemplate(data: ApiAgreementTemplate): AgreementTemplate {
  return {
    id: data.id,
    version: data.version,
    body: data.body,
    changeNotes: data.change_notes,
    createdBy: data.created_by
      ? { id: data.created_by.id, name: data.created_by.name }
      : null,
    createdAt: data.created_at,
  }
}

export const agreementTemplateQueries = {
  /** Newest first; the first is the current template. */
  versions: () =>
    queryOptions({
      queryKey: apiKey("/settings/agreement-template"),
      queryFn: async ({ signal }) => {
        const { data } = await apiFetch<ApiResource<ApiAgreementTemplate[]>>(
          "/settings/agreement-template",
          { signal }
        )

        return data.map(toAgreementTemplate)
      },
    }),

  placeholders: () =>
    queryOptions({
      queryKey: apiKey("/settings/agreement-template/placeholders"),
      queryFn: async ({ signal }): Promise<AgreementPlaceholder[]> => {
        const { data } = await apiFetch<ApiResource<ApiAgreementPlaceholder[]>>(
          "/settings/agreement-template/placeholders",
          { signal }
        )

        return data.map((placeholder) => ({
          key: placeholder.key,
          label: placeholder.label,
          help: placeholder.help ?? undefined,
          section: placeholder.section ?? undefined,
          token: `{{ ${placeholder.key} }}`,
        }))
      },
      staleTime: Number.POSITIVE_INFINITY,
    }),
}

/** Publishing inserts a new version; the API answers with its id alone. */
export function usePublishAgreementTemplate() {
  return useMutation({
    mutationFn: async ({ body, changeNotes }: PublishAgreementTemplate) => {
      const { data } = await apiFetch<
        ApiResource<{ id: string; version: number }>
      >("/settings/agreement-template", {
        method: "POST",
        body: { body, change_notes: changeNotes ?? null },
      })

      return { id: data.id, version: data.version }
    },
  })
}
